// Standalone helper with zero non-tree-shakeable dependencies. Lives in its
// own module (rather than `utils/string.ts`) so that webpack can keep the
// home page bundle minimal — `utils/string.ts` is co-imported with editor
// surfaces and ends up grouped with the editor chunk.
export function formatNumber(num: number, lang: "en" | "fr" = "en"): string {
  if (lang === "fr" && num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1).replace(".", ",")}\u00a0M`;
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + "M";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(0) + "K";
  } else {
    return num.toString();
  }
}

// A plain count on a pricing card: "1000" and "1 000".
export function formatCountIn(lang: "en" | "fr", count: number): string {
  return lang === "fr"
    ? count.toLocaleString("fr-FR").replace(/\u202f/g, "\u00a0")
    : String(count);
}

// A price in US dollars the way the language writes it: "$14.50", "$1,134" and
// "14,50 $", "1 134 $".
export function formatUsdIn(lang: "en" | "fr", amount: number): string {
  const digits = {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  };
  if (lang === "fr") {
    // A plain non-breaking space: Poppins has no glyph for the narrow one that
    // toLocaleString uses.
    const number = amount.toLocaleString("fr-FR", digits).replace(/\u202f/g, "\u00a0");
    return `${number}\u00a0$`;
  }
  return `$${amount.toLocaleString("en-US", digits)}`;
}

/**
 * A social counter as a platform draws it: rounded DOWN, so the label never
 * claims more than the post actually has (1,299 reads `1.2K`, never `1.3K`).
 * One decimal while the scaled number is small enough for it to mean
 * something and no trailing `.0` — `1K`, `1.2K`, `12.4K`, `999K`, `1.5M` —
 * which caps every label at five characters, narrow enough to sit centred
 * under a rail button.
 */
export function formatCompactCount(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return "0";

  const whole = Math.floor(count);
  if (whole < 1_000) return String(whole);

  const [divisor, suffix] =
    whole < 1_000_000
      ? ([1_000, "K"] as const)
      : whole < 1_000_000_000
        ? ([1_000_000, "M"] as const)
        : ([1_000_000_000, "B"] as const);

  const scaled = whole / divisor;
  // Truncate at both precisions rather than rounding, so 999_999 is `999K`
  // and not the nonsense `1000K` that a `toFixed` would produce.
  if (scaled >= 100) return `${Math.floor(scaled)}${suffix}`;

  const tenths = Math.floor(scaled * 10);
  return tenths % 10 === 0
    ? `${tenths / 10}${suffix}`
    : `${(tenths / 10).toFixed(1)}${suffix}`;
}
